import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
  type ReactNode,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';

// ============================================================================
// Collapse Component (Standalone)
// ============================================================================

export interface CollapseProps {
  /** Whether the content is expanded */
  isOpen: boolean;
  /** The content to show/hide */
  children: ReactNode;
  /** Animation duration in ms (default: 300) */
  animationDuration?: number;
  /** Additional class name */
  className?: string;
  /** Callback when collapse animation completes */
  onAnimationEnd?: () => void;
}

/**
 * Collapse - An animated collapsible container
 *
 * @example
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 *
 * <button onClick={() => setIsOpen(!isOpen)}>Toggle</button>
 * <Collapse isOpen={isOpen}>
 *   <div>Collapsible content here</div>
 * </Collapse>
 * ```
 */
export function Collapse({
  isOpen,
  children,
  animationDuration = 300,
  className = '',
  onAnimationEnd,
}: CollapseProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | 'auto'>(isOpen ? 'auto' : 0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    if (isOpen) {
      // Opening: measure content height, animate from 0 to that height
      const contentHeight = content.scrollHeight;
      setHeight(0);
      setIsAnimating(true);

      // Force reflow to ensure the initial height is applied
      content.offsetHeight;

      requestAnimationFrame(() => {
        setHeight(contentHeight);
      });

      const timer = setTimeout(() => {
        setHeight('auto');
        setIsAnimating(false);
        onAnimationEnd?.();
      }, animationDuration);

      return () => clearTimeout(timer);
    } else {
      // Closing: measure current height, animate from that to 0
      const contentHeight = content.scrollHeight;
      setHeight(contentHeight);
      setIsAnimating(true);

      // Force reflow
      content.offsetHeight;

      requestAnimationFrame(() => {
        setHeight(0);
      });

      const timer = setTimeout(() => {
        setIsAnimating(false);
        onAnimationEnd?.();
      }, animationDuration);

      return () => clearTimeout(timer);
    }
  }, [isOpen, animationDuration, onAnimationEnd]);

  const style: CSSProperties = {
    height: height === 'auto' ? 'auto' : `${height}px`,
    overflow: isAnimating || !isOpen ? 'hidden' : 'visible',
    transition: isAnimating ? `height ${animationDuration}ms ease` : 'none',
  };

  return (
    <div
      ref={contentRef}
      className={`collapse-content ${isOpen ? 'show' : ''} ${className}`}
      style={style}
      aria-hidden={!isOpen}
    >
      {children}
    </div>
  );
}

// ============================================================================
// Accordion Context
// ============================================================================

interface AccordionContextValue {
  openItems: Set<string>;
  toggleItem: (id: string) => void;
  allowMultiple: boolean;
}

const AccordionContext = createContext<AccordionContextValue | null>(null);

function useAccordionContext() {
  return useContext(AccordionContext);
}

// ============================================================================
// Accordion Container
// ============================================================================

export interface AccordionProps {
  /** Accordion items */
  children: ReactNode;
  /** Allow multiple items to be open at once (default: false) */
  allowMultiple?: boolean;
  /** Initially open item IDs */
  defaultOpenItems?: string[];
  /** Controlled open items */
  openItems?: string[];
  /** Callback when items change */
  onOpenItemsChange?: (openItems: string[]) => void;
  /** Additional class name */
  className?: string;
}

/**
 * Accordion - A container for collapsible items where only one (or multiple) can be open
 *
 * @example
 * ```tsx
 * <Accordion>
 *   <AccordionItem id="item1">
 *     <AccordionTrigger>Section 1</AccordionTrigger>
 *     <AccordionContent>Content for section 1</AccordionContent>
 *   </AccordionItem>
 *   <AccordionItem id="item2">
 *     <AccordionTrigger>Section 2</AccordionTrigger>
 *     <AccordionContent>Content for section 2</AccordionContent>
 *   </AccordionItem>
 * </Accordion>
 * ```
 */
export function Accordion({
  children,
  allowMultiple = false,
  defaultOpenItems = [],
  openItems: controlledOpenItems,
  onOpenItemsChange,
  className = '',
}: AccordionProps) {
  const [internalOpenItems, setInternalOpenItems] = useState<Set<string>>(
    () => new Set(defaultOpenItems)
  );

  const isControlled = controlledOpenItems !== undefined;
  const openItems = isControlled ? new Set(controlledOpenItems) : internalOpenItems;

  const toggleItem = useCallback(
    (id: string) => {
      const newOpenItems = new Set(openItems);

      if (newOpenItems.has(id)) {
        newOpenItems.delete(id);
      } else {
        if (!allowMultiple) {
          newOpenItems.clear();
        }
        newOpenItems.add(id);
      }

      if (!isControlled) {
        setInternalOpenItems(newOpenItems);
      }

      onOpenItemsChange?.(Array.from(newOpenItems));
    },
    [openItems, allowMultiple, isControlled, onOpenItemsChange]
  );

  const contextValue: AccordionContextValue = {
    openItems,
    toggleItem,
    allowMultiple,
  };

  return (
    <AccordionContext.Provider value={contextValue}>
      <div className={`accordion ${className}`}>{children}</div>
    </AccordionContext.Provider>
  );
}

// ============================================================================
// Accordion Item Context
// ============================================================================

interface AccordionItemContextValue {
  id: string;
  isOpen: boolean;
  toggle: () => void;
}

const AccordionItemContext = createContext<AccordionItemContextValue | null>(null);

function useAccordionItemContext() {
  const context = useContext(AccordionItemContext);
  if (!context) {
    throw new Error('AccordionItem components must be used within an AccordionItem');
  }
  return context;
}

// ============================================================================
// Accordion Item
// ============================================================================

export interface AccordionItemProps {
  /** Unique identifier for this item */
  id: string;
  /** Item content (trigger and content) */
  children: ReactNode;
  /** Additional class name */
  className?: string;
}

/**
 * AccordionItem - A single collapsible item in an accordion
 */
export function AccordionItem({ id, children, className = '' }: AccordionItemProps) {
  const accordionContext = useAccordionContext();

  // Support standalone usage without Accordion parent
  const [standaloneOpen, setStandaloneOpen] = useState(false);

  const isOpen = accordionContext ? accordionContext.openItems.has(id) : standaloneOpen;

  const toggle = useCallback(() => {
    if (accordionContext) {
      accordionContext.toggleItem(id);
    } else {
      setStandaloneOpen((prev) => !prev);
    }
  }, [accordionContext, id]);

  const itemContext: AccordionItemContextValue = {
    id,
    isOpen,
    toggle,
  };

  return (
    <AccordionItemContext.Provider value={itemContext}>
      <div className={`accordion-item ${className}`} style={{ borderBottom: '1px solid #dee2e6' }}>
        {children}
      </div>
    </AccordionItemContext.Provider>
  );
}

// ============================================================================
// Accordion Trigger
// ============================================================================

export interface AccordionTriggerProps {
  /** Trigger content */
  children: ReactNode;
  /** Additional class name */
  className?: string;
}

/**
 * AccordionTrigger - The clickable header that toggles the accordion item
 */
export function AccordionTrigger({ children, className = '' }: AccordionTriggerProps) {
  const { id, isOpen, toggle } = useAccordionItemContext();

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        toggle();
      }
    },
    [toggle]
  );

  return (
    <h2 className="accordion-header" style={{ margin: 0 }}>
      <button
        type="button"
        className={`accordion-button ${isOpen ? '' : 'collapsed'} ${className}`}
        onClick={toggle}
        onKeyDown={handleKeyDown}
        aria-expanded={isOpen}
        aria-controls={`collapse-${id}`}
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          padding: '1rem 1.25rem',
          fontSize: '1rem',
          color: '#212529',
          textAlign: 'left',
          backgroundColor: isOpen ? '#e7f1ff' : '#fff',
          border: 0,
          borderRadius: 0,
          overflow: 'anchor',
          cursor: 'pointer',
          transition: 'background-color 0.15s ease-in-out',
        }}
      >
        {children}
        <span
          style={{
            marginLeft: 'auto',
            width: '1.25rem',
            height: '1.25rem',
            backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23212529'%3e%3cpath fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3e%3c/svg%3e")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundSize: '1.25rem',
            transform: isOpen ? 'rotate(-180deg)' : 'rotate(0)',
            transition: 'transform 0.2s ease-in-out',
            flexShrink: 0,
          }}
          aria-hidden="true"
        />
      </button>
    </h2>
  );
}

// ============================================================================
// Accordion Content
// ============================================================================

export interface AccordionContentProps {
  /** Content to show when expanded */
  children: ReactNode;
  /** Animation duration in ms (default: 300) */
  animationDuration?: number;
  /** Additional class name */
  className?: string;
}

/**
 * AccordionContent - The collapsible content area of an accordion item
 */
export function AccordionContent({
  children,
  animationDuration = 300,
  className = '',
}: AccordionContentProps) {
  const { id, isOpen } = useAccordionItemContext();

  return (
    <Collapse isOpen={isOpen} animationDuration={animationDuration}>
      <div
        id={`collapse-${id}`}
        className={`accordion-collapse ${className}`}
        role="region"
        aria-labelledby={`heading-${id}`}
      >
        <div className="accordion-body" style={{ padding: '1rem 1.25rem' }}>
          {children}
        </div>
      </div>
    </Collapse>
  );
}

// ============================================================================
// useCollapse Hook
// ============================================================================

export interface UseCollapseOptions {
  /** Initial open state (default: false) */
  defaultOpen?: boolean;
}

export interface UseCollapseReturn {
  /** Whether the collapse is open */
  isOpen: boolean;
  /** Toggle the collapse open/closed */
  toggle: () => void;
  /** Open the collapse */
  open: () => void;
  /** Close the collapse */
  close: () => void;
  /** Props to spread on the trigger element */
  triggerProps: {
    onClick: () => void;
    'aria-expanded': boolean;
  };
}

/**
 * useCollapse - A hook for managing collapse state
 *
 * @example
 * ```tsx
 * const { isOpen, triggerProps } = useCollapse();
 *
 * <button {...triggerProps}>Toggle</button>
 * <Collapse isOpen={isOpen}>
 *   <div>Content</div>
 * </Collapse>
 * ```
 */
export function useCollapse({ defaultOpen = false }: UseCollapseOptions = {}): UseCollapseReturn {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  return {
    isOpen,
    toggle,
    open,
    close,
    triggerProps: {
      onClick: toggle,
      'aria-expanded': isOpen,
    },
  };
}

export default Collapse;
